import React, {useState} from 'react';
import {ArrowRightIcon} from '@heroicons/react/24/outline';
import {useNavigate, useParams} from 'react-router';

import {Button, Card, CardContent, CardFooter, CardHeader} from '@design-system';

import useKeyDuties from '../Queries/useKeyDuties';
import useProSectors from '../Queries/useProSectors';
import {useAuth} from '../Auth/useAuth';
import {useOnboarding} from '../Onboarding/useOnboarding';
import {findByRouteKey, useCanonicalRouteParams} from '../Util/routeIdentifier';
import {RESOURCE_GRID_CLASSES, ResourceNotice, ResourceQueryState} from './LoadingCard';
import {RESOURCE_TITLE_CLASSES, ResourceBreadcrumbs} from './ProCategories';

const FILTER_SWITCH_ID = 'filter-key-duties-switch';
const ALL_STAGES_HEADING_ID = 'pro-all-stage-duties-heading';

const PILL_CLASSES = 'inline-flex rounded-full px-3 py-1 text-xs font-medium break-words';

/**
 * A duty's obligation. The two obligations differ in their text, so the fill
 * is emphasis rather than the only signal.
 */
export const KeyDutyObligation = ({obligation}) => (
  <span
    data-testid="key-duty-obligation"
    className={`${PILL_CLASSES} ${
      obligation === 'Must (duty)'
        ? 'bg-(--compass-primary) text-(--compass-on-primary)'
        : 'border border-(--compass-primary) text-(--compass-on-surface)'
    }`}>
    {obligation}
  </span>
);

const KeyDutiesGrid = ({keyDuties, sectors, titleAs: Title}) => {
  const navigate = useNavigate();

  return (
    <ul data-testid="pro-key-duty-grid" className={RESOURCE_GRID_CLASSES}>
      {keyDuties.map((keyDuty) => (
        <li key={`key-duty-${keyDuty.id}`} className="min-w-0">
          <Card data-testid="pro-key-duty-card" className="flex h-full min-w-0 flex-col">
            <CardHeader className="gap-3">
              <Title className="m-0 min-w-0 text-base font-semibold break-words">
                {keyDuty.title}
              </Title>
              <div className="flex flex-wrap gap-2">
                <KeyDutyObligation obligation={keyDuty.obligation} />
                <span
                  data-testid="key-duty-sector"
                  className={`${PILL_CLASSES} bg-(--compass-surface-selected) text-(--compass-on-surface)`}>
                  {sectors.find((sector) => sector.id === keyDuty.professional_sector_id)?.name ??
                    'All sectors'}
                </span>
              </div>
            </CardHeader>
            <CardContent className="flex-1" />
            <CardFooter>
              <Button
                variant="outline"
                className="min-h-11"
                data-testid={keyDuty.title}
                onClick={() => navigate(`duty/${encodeURIComponent(keyDuty.slug)}`)}>
                Learn More<span className="sr-only"> about {keyDuty.title}</span>
                <ArrowRightIcon className="size-5" aria-hidden="true" />
              </Button>
            </CardFooter>
          </Card>
        </li>
      ))}
    </ul>
  );
};

const ProKeyDutiesStage = () => {
  const {data: keyDuties, error, refetch} = useKeyDuties();
  const {data: sectors, error: sectorsError, refetch: refetchSectors} = useProSectors();
  const {stage: stageKey} = useParams();
  const {authState} = useAuth();
  const {onboardingState, fetchStages} = useOnboarding();
  const [isFiltering, setIsFiltering] = useState(true);

  // Found by its slug, or by the numeric id of an address from before COM-478, which
  // becomes the slug one in place. The duties are the stage's by its id, so none is
  // listed until the stage is known: the stages' own load, failure or not-found shows.
  const stages = onboardingState?.stages;
  const stage = findByRouteKey(stages, stageKey);
  useCanonicalRouteParams({stage: stage?.slug}, {resource: 'stage'});
  const stageNotFound = stages !== undefined && !stage;

  // Filtering keeps the Professional's own sector and the duties that apply to
  // every sector; turning it off widens to every sector. Nothing is written back.
  const matchesFilter = (keyDuty) =>
    !isFiltering ||
    keyDuty.professional_sector_id === authState.user.profile.professional_sector_id ||
    keyDuty.professional_sector_id === null;

  const keyDutiesForCurrentStage = (keyDuties ?? []).filter(
    (keyDuty) => keyDuty.stage_id === stage?.id && matchesFilter(keyDuty)
  );
  const keyDutiesForAllStages = (keyDuties ?? []).filter(
    (keyDuty) => keyDuty.stage_id === null && matchesFilter(keyDuty)
  );

  return (
    <div data-testid="pro-key-duties-stage">
      <ResourceBreadcrumbs
        items={[
          {label: 'Home', to: '/dashboard'},
          {label: 'Key Duties', to: '/key-duties'},
          ...(stage ? [{label: stage.medium_label}] : []),
        ]}
      />
      <div className="mb-6 flex flex-col-reverse gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h1 className={`mb-2 ${RESOURCE_TITLE_CLASSES}`}>
            {stage ? `${stage.medium_label} duties` : 'Key duties'}
          </h1>
          {stageNotFound ? null : (
            <p className="m-0 text-(--compass-on-surface)">
              {isFiltering
                ? 'These are the key duties for your sector.'
                : 'These are the key duties for all sectors.'}
            </p>
          )}
        </div>
        {/* The label wraps the switch so the whole row is one 44px pointer target. */}
        {stageNotFound ? null : (
          <label
            htmlFor={FILTER_SWITCH_ID}
            data-testid="filter-key-duties-label"
            className="flex min-h-11 shrink-0 cursor-pointer items-center gap-3 text-(--compass-on-surface)">
            <input
              type="checkbox"
              role="switch"
              id={FILTER_SWITCH_ID}
              data-testid={FILTER_SWITCH_ID}
              checked={!isFiltering}
              // Only an explicit attribute is readable from the DOM, which is what
              // the journey asserts on now that the MUI checked-state class is gone.
              aria-checked={!isFiltering}
              onChange={() => setIsFiltering(!isFiltering)}
              className="size-6 shrink-0 accent-(--compass-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)"
            />
            Show duties for other sectors
          </label>
        )}
      </div>
      <ResourceQueryState
        data={stages}
        error={onboardingState?.stagesFailed}
        refetch={fetchStages}
        label="transition stages"
        placeholderCount={3}>
        {stageNotFound ? (
          <ResourceNotice>Stage not found.</ResourceNotice>
        ) : (
          <ResourceQueryState
            data={keyDuties}
            error={error}
            refetch={refetch}
            label="key duties"
            placeholderCount={3}>
            {/* Sector names label every card, so the grids wait for both queries
                while each keeps its own announced failure and Retry. */}
            <ResourceQueryState
              data={sectors}
              error={sectorsError}
              refetch={refetchSectors}
              label="professional sectors"
              placeholderCount={3}>
              {keyDutiesForCurrentStage.length ? (
                <KeyDutiesGrid
                  keyDuties={keyDutiesForCurrentStage}
                  sectors={sectors}
                  titleAs="h2"
                />
              ) : (
                <ResourceNotice>You have no key duties for this stage.</ResourceNotice>
              )}
              <section
                aria-labelledby={ALL_STAGES_HEADING_ID}
                className="mt-8 border-t border-(--compass-border) pt-6">
                <h2
                  id={ALL_STAGES_HEADING_ID}
                  className="m-0 mb-2 text-xl font-semibold text-(--compass-on-surface)">
                  All stage duties
                </h2>
                <p className="m-0 mb-4 text-(--compass-on-surface)">
                  These duties apply to all stages of the transition process.
                </p>
                {keyDutiesForAllStages.length ? (
                  <KeyDutiesGrid keyDuties={keyDutiesForAllStages} sectors={sectors} titleAs="h3" />
                ) : (
                  <ResourceNotice>No duties apply to all stages.</ResourceNotice>
                )}
              </section>
            </ResourceQueryState>
          </ResourceQueryState>
        )}
      </ResourceQueryState>
    </div>
  );
};

export default ProKeyDutiesStage;
